const { debounce, getElement, getElements } = window.BAO.Utils;
class Cache {
  constructor() {
    /** @type {Map<String, String>} */
    this.cache = new Map();
  }

  exists(key) {
    return this.cache.has(key);
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set(key, value) {
    this.cache.set(key, value);
  }

  /**
   * @param {String} key
   */
  get(key) {
    return this.cache.get(key);
  }

  delete(key) {
    this.cache.delete(key);
  }
}

class DataFetcher {
  constructor() {
    this.cache = new Cache();
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch(url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.fetchData(url, section);
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async fetchData(url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url);

    this.cache.set(url, html);

    return html;
  }
}

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor(form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form;

    this.dataFetcher = new DataFetcher();
  }

  /**
   * @param {String} url
   * @param {InputEvent} event
   * @param {String} section
   * @param {'NEXT'|'PREVIOUS'|''} [direction]
   *
   * @returns {void}
   */
  async render(url, event, { section = "", direction = "" } = {}) {
    const html = await this.dataFetcher.fetch(url, section);

    this.form.log("render", { url, html });

    const newHTMLDom = new DOMParser().parseFromString(html, "text/html");

    this.renderFilters(newHTMLDom, event);
    this.renderProductGridContainer(newHTMLDom, direction);
    this.renderProductCount(newHTMLDom);
    this.renderLoadMoreState(newHTMLDom);
    this.renderLoadPreviousState(newHTMLDom);
    this.renderProductRangeButtons(newHTMLDom);

    this.form.loading = false;
  }

  /**
   * @param {Document} document
   * @param {InputEvent|SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters(document, e) {
    const detailSelector = [
      this.form.els?.facet?.selector,
      this.form.externalEls?.facet?.selector,
    ]
      .filter(Boolean)
      .join(",");
    const facetDetailElements = getElements(detailSelector, {
      context: document,
    });
    /**
     * @param {HTMLElement} element
     *
     * @returns {Boolean}
     */
    const matchesIndex = (element) => {
      const facet = e ? e.target.closest(detailSelector) : null;

      return facet ? element.dataset.index === facet.dataset.index : false;
    };
    const facetsToRender = facetDetailElements.filter(
      (el) => !matchesIndex(el),
    );
    const countsToRender = facetDetailElements.find((el) => matchesIndex(el));

    this.form.log("renderFilters", {
      e,
      parsedHTML: document,
      facetDetailElements,
      facetsToRender,
      countsToRender,
    });

    facetsToRender.forEach((element) => {
      const selector = detailSelector
        .split(",")
        .map((selector) => `${selector}[data-index="${element.dataset.index}"]`)
        .join(",");

      const el = getElement(selector);

      el.innerHTML = element.innerHTML;
    });

    this.renderActiveFacets(document);
    this.renderAdditionalElements(document);
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderActiveFacets(parsedHTML) {
    this.activeFacetSelectors.forEach((selector) => {
      const activeFacetElement = parsedHTML.querySelector(selector);

      this.form.log("renderActiveFacets", {
        parsedHTML,
        activeFacetElement,
        selector,
      });

      if (!activeFacetElement) return;

      getElements(selector).forEach(
        (el) => (el.innerHTML = activeFacetElement.innerHTML),
      );
    });
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderAdditionalElements(document) {}

  /**
   * @param {Document} document
   * @param {'NEXT'|'PREVIOUS'|''} [direction]
   *
   * @returns {void}
   */
  renderProductGridContainer(document, direction = "") {
    const newItems = window.BAO.Utils.getElements(
      this.form.externalEls.item.selector,
      { context: document },
    );
    if (!newItems) return;

    this.form.log("renderProductGridContainer", { newItems, direction });

    const container = this.form.externalEls["product-grid"].element;
    const fragment = new DocumentFragment();

    newItems.forEach((item) => fragment.appendChild(item));

    if (direction === "NEXT") {
      container.appendChild(fragment);
    } else if (direction === "PREVIOUS") {
      container.prepend(fragment);
    } else {
      container.replaceChildren(...fragment.childNodes);
    }
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderProductCount(document) {
    const count = window.BAO.Utils.getElement(
      this.form?.externalEls?.["pagination-count"]?.selector,
      { context: document },
    ).innerHTML;

    this.form.log("renderProductCount", {
      html: document,
      count,
      countElements: this.countElements,
    });
    if (!count) return;

    this.form?.externalEls?.["pagination-count"]?.elements?.forEach(
      (element) => (element.innerHTML = count),
    );
  }

  /**
   * @param {Document} document
   */
  renderLoadMoreState(document) {
    window.yotpo?.refreshWidgets();

    const loadMore = document.querySelector(
      this.form?.externalEls["load-more"]?.selector,
    );

    this.form.log("renderLoadMoreState", { loadMore });

    if (!loadMore) return;

    if (this.shouldRerenderLoadMore) {
      const [path, searchString] = loadMore.getAttribute("href").split("?");
      const searchParams = new URLSearchParams(searchString);
      searchParams.page = this.form.currentHighestRenderedPage;

      const isDisabled = loadMore.hasAttribute("disabled");

      this.form.externalEls["load-more"].element.setAttribute(
        "href",
        `${path}?${searchParams.toString()}`,
      );
      this.form.externalEls["load-more"].element.toggleAttribute(
        "disabled",
        isDisabled,
      );

      if (isDisabled) {
        this.form.externalEls["load-more"].element.innerText =
          window.theme.strings.pagination.showingAllProducts;
      } else {
        this.form.externalEls["load-more"].element.innerText =
          window.theme.strings.pagination.loadMore;
      }
    }
  }

  /**
   * @param {Document} document
   */
  renderLoadPreviousState(document) {
    window.yotpo?.refreshWidgets();

    if (!this.shouldShowLoadPrevious) {
      this.form.externalEls["pagination-load-previous"].element.innerHTML = "";

      return;
    }

    const loadPrevious = document.querySelector(
      this.form?.externalEls["load-previous"]?.selector,
    );

    this.form.log("renderLoadPreviousState", { loadPrevious });

    if (!loadPrevious) return;

    if (this.loadPreviousIsCurrentlyHidden) {
      this.form.externalEls["pagination-load-previous"].element.innerHTML =
        document.querySelector(
          this.form?.externalEls["pagination-load-previous"]?.selector,
        ).innerHTML;

      return;
    }

    if (this.shouldRerenderLoadPrevious) {
      const [path, searchString] = loadPrevious.getAttribute("href").split("?");
      const searchParams = new URLSearchParams(searchString);
      searchParams.page = this.form.currentLowestRenderedPage;

      const isDisabled = loadPrevious.hasAttribute("disabled");
      const url = searchParams.toString()
        ? `${path}?${searchParams.toString()}`
        : path;

      this.form.externalEls["load-previous"].element.setAttribute("href", url);
      this.form.externalEls["load-previous"].element.toggleAttribute(
        "disabled",
        isDisabled,
      );
    }
  }

  renderProductRangeButtons(parsedHTML) {
    if (!this.form.externalEls?.["product-range-button"]?.exists) return;

    const buttonElements = getElements(
      this.form.externalEls?.["product-range-button"]?.selector,
      {
        context: document,
      },
    );
    const newButtonElements = parsedHTML.querySelectorAll(
      this.form.externalEls?.["product-range-button"]?.selector,
    );

    newButtonElements.forEach((newButton, index) => {
      const buttonElement = buttonElements[index];
      if (!buttonElement) return;
      buttonElement.disabled = newButton.disabled;
      buttonElement.checked = newButton.checked;
    });
  }

  /**
   * @returns {Element[]}
   */
  get countElements() {
    return getElements("[data-product-grid-count]");
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors() {
    if ("active-facets" in this.form.externalEls) {
      return [
        this.form.els["active-facets"].selector,
        this.form.externalEls["active-facets"].selector,
      ];
    }

    return [this.form.els["active-facets"].selector];
  }

  get shouldShowLoadPrevious() {
    const anElementFromTheFirstPageExists = !!window.BAO.Utils.getElement(
      `${this.form.externalEls.item?.selector}[data-current-page="1"]`,
    );
    if (anElementFromTheFirstPageExists) return false;

    return this.currentPage > 1;
  }

  get loadPreviousIsCurrentlyHidden() {
    return !this.form.externalEls["pagination-load-previous"]?.element
      ?.innerHTML;
  }

  get shouldRerenderLoadPrevious() {
    return this.currentPage < this.form.currentLowestRenderedPage;
  }

  get shouldRerenderLoadMore() {
    return this.currentPage >= this.form.currentHighestRenderedPage;
  }

  get currentPage() {
    const params = this.form.searchParams.current;
    const page = params.get("page");

    return Number(page) || 1;
  }

  /**
   * @returns {number}
   */
  get lastPage() {
    return Number(this.form.externalEls.item.element.dataset.lastPage);
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

export class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static isRunning = false;

  static get requiredElements() {
    return ["facets", "facet", "facet-summary"];
  }

  static get requiredExternalElements() {
    return [
      "container",
      "product-grid",
      "pagination-load-more",
      "pagination-load-previous",
      "item",
    ];
  }

  constructor() {
    super();

    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: "template-collection-product-grid",
        section: this.dataset.id,
        selector: ".shopify-section",
      },
    ];

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    };

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this);

    this.currentLowestRenderedPage =
      Number(this.externalEls.item.element.dataset.currentPage) || 1;
    this.currentHighestRenderedPage = this.currentLowestRenderedPage;
  }

  setupListeners() {
    super.setupListeners();

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this);

    this.debouncedOnSubmit = debounce((e) => this.onSubmit(e), 500);
    this.listeners.add(this, "input", this.debouncedOnSubmit.bind(this));

    this.externalEls?.["product-range-button"]?.elements.forEach((input) => {
      this.listeners.add(input, "change", this.debouncedOnSubmit.bind(this));
    });

    this.listeners.add(window, "popstate", this.onHistoryChange.bind(this));
    this.listeners.add(
      this.els.facets.element,
      "keyup",
      this.onKeyUp.bind(this),
    );

    this.els.facet.elements.forEach((el) => {
      this.listeners.add(el, "toggle", this.onFacetToggle.bind(this));
    });

    this.listeners.add(document, "click", this.onLoadMoreClick.bind(this));
    this.listeners.add(document, "click", this.onLoadPreviousClick.bind(this));
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange(e) {
    const searchParams = e.state
      ? e.state.searchParams
      : this.form.searchParams.initial;
    if (
      searchParams.toString() === this.form.searchParams.previous.toString()
    ) {
      return;
    }

    this.form.renderPage({ searchParams, updateURLHash: false });
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick(e) {
    e.preventDefault();

    const searchParams = e.currentTarget.href.includes("?")
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf("?") + 1)
      : "";

    this.log("onFacetRemovalClick", { searchParams });

    this.renderPage({ searchParams });
  }

  /**
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  onKeyUp(e) {
    if (e.code.toUpperCase() !== "ESCAPE") return;

    const openFacet = e.target.closest(`${this.els.facet.selector}[open]`);
    if (!openFacet) return;

    const summary = getElement(`${this.els["facet-summary"].selector}`, {
      context: openFacet,
    });
    openFacet.removeAttribute("open");
    summary.setAttribute("aria-expanded", "false");
    summary?.focus();
  }

  /**
   * @param {Event} e
   *
   * @returns {void}
   */
  onFacetToggle(e) {
    /**
     * @type {HTMLDetailsElement}
     */
    const facet = e.currentTarget;

    this.els.facet.elements.forEach((el) =>
      el.setAttribute("aria-current", (facet.open && facet === el).toString()),
    );

    if (facet.open) {
      this.els.facets.element.setAttribute("data-busy", "");
    } else {
      this.els.facets.element.removeAttribute("data-busy");
    }
  }

  onLoadMoreClick(event) {
    if (!this.isActive) return;
    if (
      event.target.closest(this.externalEls?.["load-more"]?.selector) == null
    ) {
      return;
    }

    event.preventDefault();

    const anchor = event.target.closest("a");
    this.log("onLoadMoreClick", { anchor });
    if (
      !anchor ||
      anchor.getAttribute("href") === "" ||
      anchor.hasAttribute("disabled")
    ) {
      return;
    }
    const href = anchor.getAttribute("href");
    this.log("onLoadMoreClick", { href });

    const paginationSearchParams = new URLSearchParams(href.split("?")[1]);
    const wantedPage = paginationSearchParams.get("page") ?? "1";

    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    );
    currentSearchParams.set("page", wantedPage);

    this.renderPage({
      searchParams: currentSearchParams.toString(),
      direction: "NEXT",
    });

    this.constructor.isRunning = false;
  }

  onLoadPreviousClick(event) {
    if (!this.isActive) return;
    if (
      event.target.closest(this.externalEls?.["load-previous"]?.selector) ==
      null
    ) {
      return;
    }

    event.preventDefault();

    const anchor = event.target.closest("a");
    this.log("onLoadPreviousClick", { anchor });
    if (
      !anchor ||
      anchor.getAttribute("href") === "" ||
      anchor.hasAttribute("disabled")
    ) {
      return;
    }
    const href = anchor.getAttribute("href");
    this.log("onLoadPreviousClick", { href });

    const paginationSearchParams = new URLSearchParams(href.split("?")[1]);
    const wantedPage = paginationSearchParams.get("page") ?? "1";

    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    );
    currentSearchParams.set("page", wantedPage);

    this.renderPage({
      searchParams: currentSearchParams.toString(),
      direction: "PREVIOUS",
    });
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this);

    // Checks external inputs. If found, their values are appended to the form data.
    // This is used for the Core/Elite/Pro filter buttons used on some collection templates.
    this.externalEls?.["product-range-button"]?.elements.forEach((input) => {
      if (input.name && input.value && input.checked) {
        formData.append(input.name, input.value);
      }
    });

    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString();

    this.renderPage({ searchParams, e });
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent} [e]
   * @param {Boolean} [updateURLHash]
   * @param {'NEXT'|'PREVIOUS'|''} [direction]
   *
   * @returns {void}
   */
  renderPage({ searchParams, e, updateURLHash = true, direction = "" } = {}) {
    this.log("renderPage", { searchParams, e, updateURLHash });

    this.searchParams.previous = this.searchParams.current;
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams);

    this.loading = true;

    this.sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`;

      this.log("renderPage", { section, url });

      this.renderer.render(url, e, { section: section.section, direction });
    });

    if (updateURLHash) this.updateURLHash(this.searchParams.current);
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash(searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      "",
      `${window.location.pathname}${
        searchParams && "?".concat(searchParams.toString())
      }`,
    );
  }

  /**
   * @returns {Boolean}
   */
  get loading() {
    return this._loading;
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading(val) {
    this._loading = val;

    this.setAttribute("aria-busy", val.toString());

    this.externalEls?.container.element.setAttribute(
      "aria-busy",
      val.toString(),
    );
  }

  get isActive() {
    return this.offsetParent != null;
  }
}

if (!customElements.get("collection-filters-form")) {
  customElements.define("collection-filters-form", CollectionFiltersForm, {
    extends: "form",
  });
}

class PriceRange extends window.BAO.CustomElement() {
  static get requiredElements() {
    return ["min", "max"];
  }

  constructor() {
    super();

    this.setMinAndMaxValues();
  }

  setupListeners() {
    super.setupListeners();

    this.onRangeChange = this.onRangeChange.bind(this);

    getElements("input", { context: this }).forEach((el) =>
      this.listeners.add(el, "change", this.onRangeChange),
    );
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onRangeChange(e) {
    this.adjustToValidValues(e.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    if (this.maxInput.value) {
      this.minInput.setAttribute("max", this.maxInput.value);
    }
    if (this.minInput.value) {
      this.maxInput.setAttribute("min", this.minInput.value);
    }
    if (this.minInput.value === "") this.maxInput.setAttribute("min", "0");
    if (this.maxInput.value === "") {
      this.minInput.setAttribute("max", this.maxInput.getAttribute("max"));
    }
  }

  /**
   * @param {HTMLInputElement} input
   *
   * @returns {void}
   */
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInput() {
    return this.els.max.element;
  }

  /**
   * @returns {HTMLInputElement}
   */
  get minInput() {
    return this.els.min.element;
  }
}

if (!customElements.get("price-range")) {
  customElements.define("price-range", PriceRange);
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners() {
    super.setupListeners();

    this.listeners.add(this.querySelector("a"), "click", (e) => {
      e.preventDefault();

      this.form.onFacetRemovalClick(e);
    });
  }

  get form() {
    const selector = 'collection-filters-form, [is="collection-filters-form"]';

    return this.closest(selector) || document.querySelector(selector);
  }
}

if (!customElements.get("facet-remove")) {
  customElements.define("facet-remove", FacetRemove);
}
